import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, Tag, Hash } from 'lucide-react';
import { EduTag, CreateTagRequest, UpdateTagRequest } from '../../../types/learning';
import { API_CONFIG } from '../../../config/api';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const TagManagement: React.FC = () => {
  const [tags, setTags] = useState<EduTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState<EduTag | null>(null);
  const [formData, setFormData] = useState<CreateTagRequest>({
    name: '',
    description: '',
    type: 'general'
  });

  const tagTypes = [
    { value: 'general', label: '通用标签' },
    { value: 'difficulty', label: '难度标签' },
    { value: 'category', label: '分类标签' },
    { value: 'skill', label: '技能标签' },
    { value: 'topic', label: '主题标签' }
  ];

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-tags-unified-manage?mode=admin&action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch tags');

      const result = await response.json();
      if (result.success) {
        setTags(result.data.data || []);
      }
    } catch (error) {
      console.error('获取标签列表失败:', error);
      toast.error('获取标签列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTag(null);
    setFormData({
      name: '',
      description: '',
      type: 'general'
    });
    setShowModal(true);
  };

  const handleEdit = (tag: EduTag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description || '',
      type: tag.type || 'general'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('请输入标签名称');
      return;
    }

    try {
      const url = editingTag 
        ? `${API_CONFIG.FUNCTIONS_URL}/edu-tags-unified-manage?mode=admin&action=update`
        : `${API_CONFIG.FUNCTIONS_URL}/edu-tags-unified-manage?mode=admin&action=create`;
      
      const body = editingTag 
        ? { ...formData, id: editingTag.id } as UpdateTagRequest
        : formData;

      const response = await fetch(url, {
        method: editingTag ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Failed to save tag');

      const result = await response.json();
      if (result.success) {
        toast.success(editingTag ? '标签更新成功' : '标签创建成功');
        setShowModal(false);
        await fetchTags();
      } else {
        throw new Error(result.error || 'Save failed');
      }
    } catch (error) {
      console.error('保存标签失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    }
  };

  const handleDelete = async (tag: EduTag) => {
    if (!confirm(`确定要删除标签「${tag.name}」吗？此操作不可撤销。`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-tags-unified-manage?mode=admin&action=delete&id=${tag.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to delete tag');

      const result = await response.json();
      if (result.success) {
        toast.success('标签删除成功');
        await fetchTags();
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      console.error('删除标签失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleToggleStatus = async (tag: EduTag) => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-tags-unified-manage?mode=admin&action=update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: tag.id,
          status: tag.status === 1 ? 0 : 1
        })
      });

      if (!response.ok) throw new Error('Failed to toggle status');

      const result = await response.json();
      if (result.success) {
        toast.success(`标签已${tag.status === 1 ? '禁用' : '启用'}`);
        await fetchTags();
      }
    } catch (error) {
      console.error('切换状态失败:', error);
      toast.error('操作失败');
    }
  };

  const getTypeLabel = (type: string) => {
    const typeObj = tagTypes.find(t => t.value === type);
    return typeObj?.label || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      general: 'bg-gray-100 text-gray-800',
      difficulty: 'bg-red-100 text-red-800',
      category: 'bg-blue-100 text-blue-800',
      skill: 'bg-green-100 text-green-800',
      topic: 'bg-purple-100 text-purple-800'
    };
    return colors[type] || colors.general;
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (tag.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && tag.status === 1) ||
                         (statusFilter === 'inactive' && tag.status === 0);
    const matchesType = typeFilter === 'all' || tag.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Tag className="w-6 h-6 mr-2 text-blue-600" />
              学习标签管理
            </h2>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加标签
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索标签名称或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部类型</option>
              {tagTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="active">已启用</option>
              <option value="inactive">已禁用</option>
            </select>
          </div>

          {/* 标签统计 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{tags.length}</div>
              <div className="text-sm text-blue-600">总标签数</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">
                {tags.filter(t => t.status === 1).length}
              </div>
              <div className="text-sm text-green-600">已启用</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-600">
                {tags.filter(t => t.status === 0).length}
              </div>
              <div className="text-sm text-yellow-600">已禁用</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600">
                {tags.reduce((sum, tag) => sum + (tag.usage_count || 0), 0)}
              </div>
              <div className="text-sm text-purple-600">总使用次数</div>
            </div>
          </div>

          {/* 标签列表 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTags.map((tag) => (
              <div key={tag.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Hash className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900">{tag.name}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleEdit(tag)}
                      className="text-blue-600 hover:text-blue-900"
                      title="编辑"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(tag)}
                      className="text-red-600 hover:text-red-900"
                      title="删除"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {tag.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {tag.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(tag.type || 'general')}`}>
                      {getTypeLabel(tag.type || 'general')}
                    </span>
                    <button
                      onClick={() => handleToggleStatus(tag)}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        tag.status === 1
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {tag.status === 1 ? (
                        <><Eye className="w-3 h-3 mr-1" />启用</>
                      ) : (
                        <><EyeOff className="w-3 h-3 mr-1" />禁用</>
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">
                    使用 {tag.usage_count || 0} 次
                  </div>
                </div>
                
                <div className="mt-2 text-xs text-gray-400">
                  创建于 {new Date(tag.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>

          {filteredTags.length === 0 && (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无标签数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 创建/编辑模态框 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {editingTag ? '编辑标签' : '创建标签'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  标签名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入标签名称"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  标签类型
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {tagTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入标签描述"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingTag ? '更新' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagManagement;